import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, Plus, Trash, CheckCircle, XCircle, WarningCircle,
  Play, Flask, Funnel, FloppyDisk, Check,
} from "@phosphor-icons/react";

const STEPS = ["Source", "Destination", "Object Mapping", "Field Mapping",
  "Transform Rules", "Validation", "Test Migration", "Full Migration"];
const TRANSFORMS = ["direct", "static", "concat", "date_format", "boolean_map", "value_map", "text", "ignore"];

export default function ProjectWizard() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [maxStep, setMaxStep] = useState(0);
  const [connections, setConnections] = useState([]);
  const [projectName, setProjectName] = useState("");
  const [sourceId, setSourceId] = useState("");
  const [targetId, setTargetId] = useState("");
  const [projectId, setProjectId] = useState(pid || null);
  const [srcObjects, setSrcObjects] = useState([]);
  const [tgtObjects, setTgtObjects] = useState([]);
  const [objMaps, setObjMaps] = useState([{ source_object: "", target_object: "", order: 0 }]);
  const [selOM, setSelOM] = useState(0);
  const [fieldData, setFieldData] = useState({}); // omId -> {source_fields, target_fields, mappings}
  const [fieldErr, setFieldErr] = useState({}); // omId -> error message
  const [validation, setValidation] = useState(null);
  const [openFilters, setOpenFilters] = useState(null);
  const [srcFieldsCache, setSrcFieldsCache] = useState({});
  const [testJob, setTestJob] = useState(null);
  const [testRecords, setTestRecords] = useState([]);
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);

  const loadTemplates = () => api.get("/mapping-templates").then((r) => setTemplates(r.data)).catch(() => {});
  useEffect(() => { if (step === 3) loadTemplates(); }, [step]);

  const persistCurrentMappings = async (om) => {
    const fd = fieldData[om.id];
    if (fd) await api.put(`/object-mappings/${om.id}/field-mappings`,
      { mappings: fd.mappings.filter((m) => m.target_field) });
  };

  const saveAsTemplate = async () => {
    const om = objMaps[selOM];
    if (!om?.id) return;
    const name = window.prompt("Template name:", `${om.source_object} → ${om.target_object}`);
    if (!name) return;
    try {
      await persistCurrentMappings(om);
      await api.post("/mapping-templates", { name, object_mapping_id: om.id });
      toast.success("Mapping template saved");
      loadTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save template"); }
  };

  const applyTemplate = async (tid) => {
    const om = objMaps[selOM];
    if (!om?.id || !tid) return;
    try {
      await api.post(`/object-mappings/${om.id}/apply-template/${tid}`);
      setFieldData((fd) => { const c = { ...fd }; delete c[om.id]; return c; });
      await loadFieldMeta(om);
      toast.success("Template applied");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to apply template"); }
  };

  useEffect(() => { api.get("/connections").then((r) => setConnections(r.data)); }, []);

  useEffect(() => {
    if (pid) {
      api.get(`/projects/${pid}`).then((r) => {
        const p = r.data;
        setProjectName(p.name); setSourceId(p.source_connection_id); setTargetId(p.target_connection_id);
        setProjectId(p.id);
        if (p.object_mappings?.length) setObjMaps(p.object_mappings.map((m) => ({ ...m })));
        discover(p.source_connection_id, setSrcObjects);
        discover(p.target_connection_id, setTgtObjects);
        setStep(2);
      });
    }
  }, [pid]);

  const discover = async (cid, setter) => {
    try { const { data } = await api.get(`/connections/${cid}/objects`); setter(data); }
    catch (e) { toast.error("Discovery failed: " + (e.response?.data?.detail || "")); }
  };

  const usable = connections.filter((c) => c.status !== "disabled");

  // ---- step transitions ----
  const next = async () => {
    if (step === 1) {
      if (!projectName || !sourceId || !targetId) return toast.error("Fill all fields");
      setBusy(true);
      try {
        let id = projectId;
        if (!id) {
          const { data } = await api.post("/projects", {
            name: projectName, source_connection_id: sourceId, target_connection_id: targetId,
          });
          id = data.id; setProjectId(id);
        }
        await Promise.all([discover(sourceId, setSrcObjects), discover(targetId, setTgtObjects)]);
      } catch (e) { toast.error(e.response?.data?.detail || "Failed"); setBusy(false); return; }
      setBusy(false);
    }
    if (step === 2) {
      const valid = objMaps.filter((m) => m.source_object && m.target_object);
      if (!valid.length) return toast.error("Add at least one object mapping");
      setBusy(true);
      try {
        const { data } = await api.put(`/projects/${projectId}/object-mappings`,
          { mappings: valid.map((m, i) => ({ ...m, order: i })) });
        setObjMaps(data); setSelOM(0);
        await loadFieldMeta(data[0]);
      } catch (e) { toast.error("Failed to save mappings"); setBusy(false); return; }
      setBusy(false);
    }
    if (step === 4) {
      setBusy(true);
      try {
        for (const om of objMaps) {
          const fd = fieldData[om.id];
          if (fd) await api.put(`/object-mappings/${om.id}/field-mappings`,
            { mappings: fd.mappings.filter((m) => m.target_field) });
        }
      } catch (e) { toast.error("Failed to save field mappings"); setBusy(false); return; }
      setBusy(false);
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const runValidation = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/projects/${projectId}/validate`); setValidation(data); }
    catch (e) { toast.error("Validation failed"); }
    setBusy(false);
  };

  useEffect(() => { setMaxStep((m) => Math.max(m, step)); }, [step]);

  useEffect(() => {
    if ((step === 3 || step === 4) && objMaps[selOM]?.id) loadFieldMeta(objMaps[selOM]);
    // eslint-disable-next-line
  }, [step, selOM]);

  useEffect(() => {
    if (step === 5 && projectId && !validation) runValidation();
    // eslint-disable-next-line
  }, [step, projectId]);

  const loadFieldMeta = async (om) => {
    if (!om || fieldData[om.id]) return;
    setFieldErr((e) => { const n = { ...e }; delete n[om.id]; return n; });
    try {
      const { data } = await api.get(`/object-mappings/${om.id}/auto-suggest`);
      const saved = await api.get(`/object-mappings/${om.id}/field-mappings`);
      const mappings = saved.data.length
        ? saved.data.map((m) => ({
            source_field: m.source_field, target_field: m.target_field,
            transform: m.transform || { type: "direct" },
            source_datatype: m.source_datatype, target_datatype: m.target_datatype,
            required: m.required,
          }))
        : data.suggestions;
      setFieldData((fd) => ({ ...fd, [om.id]: {
        source_fields: data.source_fields, target_fields: data.target_fields, mappings,
      }}));
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Could not load fields from the connector.";
      setFieldErr((er) => ({ ...er, [om.id]: msg }));
      toast.error("Field discovery failed: " + msg);
    }
  };

  const selectOM = async (i) => { setSelOM(i); await loadFieldMeta(objMaps[i]); };

  const patchOM = async (omId, patch) => {
    setObjMaps((o) => o.map((x) => x.id === omId ? { ...x, ...patch } : x));
    try { await api.patch(`/object-mappings/${omId}`, patch); }
    catch (e) { toast.error("Failed to update write mode"); }
  };

  const loadSourceFields = async (obj) => {
    if (!obj || !sourceId || srcFieldsCache[obj]) return;
    try {
      const { data } = await api.get(`/connections/${sourceId}/objects/${obj}/fields`);
      setSrcFieldsCache((c) => ({ ...c, [obj]: data.fields || [] }));
    } catch (e) { /* fields optional for filters */ }
  };

  const toggleFilters = (i) => {
    const opening = openFilters !== i;
    setOpenFilters(opening ? i : null);
    if (opening) loadSourceFields(objMaps[i].source_object);
  };

  const updateMapping = (omId, idx, patch) => {
    setFieldData((fd) => {
      const m = [...fd[omId].mappings]; m[idx] = { ...m[idx], ...patch };
      return { ...fd, [omId]: { ...fd[omId], mappings: m } };
    });
  };

  const runTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/jobs", { project_id: projectId, mode: "test", sample_size: 10 });
      setTestJob(data);
      poll(data.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); setBusy(false); }
  };

  const poll = (jid) => {
    const iv = setInterval(async () => {
      const { data } = await api.get(`/jobs/${jid}`);
      setTestJob(data);
      if (["completed", "failed"].includes(data.status)) {
        clearInterval(iv); setBusy(false);
        const rec = await api.get(`/jobs/${jid}/records`); setTestRecords(rec.data);
      }
    }, 1500);
  };

  const runFull = async () => {
    try {
      const { data } = await api.post("/jobs", { project_id: projectId, mode: "full" });
      toast.success("Full migration started");
      navigate(`/jobs/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const curOM = objMaps[selOM];
  const curFD = curOM && fieldData[curOM.id];

  return (
    <Layout>
      <PageHeader title="Migration Wizard" subtitle={projectName || "Configure a new migration"} />
      <div className="p-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 max-w-7xl">
          {/* ---- Left: vertical timeline stepper ---- */}
          <aside className="md:col-span-3">
            <div className="md:sticky md:top-24 relative pl-1">
              <span className="hidden md:block absolute left-[18px] top-5 bottom-5 w-px bg-stone-200" aria-hidden />
              {STEPS.map((s, i) => {
                const clickable = i <= maxStep;
                const active = i === step;
                const done = i < step;
                return (
                  <button key={s} type="button" disabled={!clickable}
                    data-testid={`wizard-step-${i}`}
                    onClick={() => clickable && setStep(i)}
                    className={`group relative w-full flex items-center gap-3 py-2 rounded-xl transition-all duration-200 ${
                      clickable ? "cursor-pointer hover:bg-stone-100/70" : "cursor-not-allowed opacity-60"}`}>
                    <span className={`relative z-10 w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-xs font-semibold font-head transition-all duration-200 ${
                      active ? "bg-[#C2410C] text-white ring-4 ring-orange-100 shadow-glow"
                        : done ? "bg-stone-800 text-white"
                        : "bg-white text-stone-400 border border-stone-200"}`}>
                      {done ? <Check size={16} weight="bold" /> : i + 1}
                    </span>
                    <span className="min-w-0 text-left">
                      <span className="block text-[10px] uppercase tracking-[0.18em] text-stone-400">Step {i + 1}</span>
                      <span className={`block font-head text-sm truncate ${active ? "text-stone-900 font-semibold" : done ? "text-stone-600" : "text-stone-400"}`}>{s}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </aside>

          {/* ---- Right: glass content card ---- */}
          <section className="md:col-span-9">
            <div key={step} className="bg-white rounded-3xl border border-stone-100 p-8 md:p-10 shadow-float min-h-[440px] animate-stepin">
              {/* STEP 0 - Source */}
              {step === 0 && (
                <Section title="Select Source Connection" desc="Where records will be read from.">
                  <ConnSelect testid="wizard-source" value={sourceId} onChange={setSourceId} options={usable} />
                </Section>
              )}

              {/* STEP 1 - Destination */}
              {step === 1 && (
                <Section title="Select Destination & Name Project" desc="Where records will be written to.">
                  <div className="space-y-5 max-w-md">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-stone-500">Project Name</Label>
                      <Input data-testid="wizard-project-name" value={projectName} onChange={(e) => setProjectName(e.target.value)}
                        className="mt-1.5 rounded-xl" placeholder="Q3 CRM Consolidation" />
                    </div>
                    <ConnSelect testid="wizard-target" label="Destination" value={targetId} onChange={setTargetId}
                      options={usable.filter((c) => c.id !== sourceId)} />
                  </div>
                </Section>
              )}

              {/* STEP 2 - Object Mapping */}
              {step === 2 && (
                <Section title="Object Mapping" desc="Map source objects to destination objects. Order defines parent→child dependency.">
                  <div className="space-y-3">
                    {objMaps.map((m, i) => (
                      <div key={i} className="border border-stone-200 bg-stone-50/50 rounded-2xl p-4">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="font-mono text-xs text-stone-400 w-6">{i + 1}</span>
                          <ObjSelect testid={`om-src-${i}`} value={m.source_object} options={srcObjects}
                            onChange={(v) => setObjMaps((o) => o.map((x, j) => j === i ? { ...x, source_object: v } : x))} />
                          <span className="w-9 h-9 shrink-0 rounded-full bg-white border border-stone-200 flex items-center justify-center">
                            <ArrowRight size={16} className="text-[#C2410C]" weight="bold" />
                          </span>
                          <ObjSelect testid={`om-tgt-${i}`} value={m.target_object} options={tgtObjects}
                            onChange={(v) => setObjMaps((o) => o.map((x, j) => j === i ? { ...x, target_object: v } : x))} />
                          <Button data-testid={`om-filter-toggle-${i}`} variant="outline" size="sm"
                            onClick={() => toggleFilters(i)} className="gap-1.5 text-xs shrink-0">
                            <Funnel size={14} /> Filters ({(m.filters || []).length})
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setObjMaps((o) => o.filter((_, j) => j !== i))}
                            className="text-rose-600 hover:bg-rose-50"><Trash size={14} /></Button>
                        </div>
                        {openFilters === i && (
                          <FilterEditor omIndex={i} filters={m.filters || []}
                            sourceFields={srcFieldsCache[m.source_object] || []}
                            onChange={(f) => setObjMaps((o) => o.map((x, j) => j === i ? { ...x, filters: f } : x))} />
                        )}
                      </div>
                    ))}
                    <Button data-testid="add-object-mapping" variant="outline" size="sm"
                      onClick={() => setObjMaps((o) => [...o, { source_object: "", target_object: "", order: o.length, filters: [] }])}
                      className="gap-1.5 text-xs mt-2"><Plus size={14} /> Add Object Mapping</Button>
                  </div>
                </Section>
              )}

              {/* STEP 3 & 4 share object tabs */}
              {(step === 3 || step === 4) && (
                <Section
                  title={step === 3 ? "Field Mapping" : "Transformation Rules"}
                  desc={step === 3 ? "Connect each destination field to a source field. Auto-suggested matches are pre-filled." : "Choose how each field is transformed during migration."}>
                  <div className="flex gap-2 mb-5 flex-wrap">
                    {objMaps.map((om, i) => (
                      <button key={om.id} data-testid={`om-tab-${i}`} onClick={() => selectOM(i)}
                        className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${
                          i === selOM ? "bg-stone-900 text-white border-stone-900 shadow-sm"
                            : "bg-white text-stone-600 border-stone-200 hover:border-stone-300 hover:-translate-y-px"}`}>
                        {om.source_object} → {om.target_object}
                      </button>
                    ))}
                  </div>

                  {step === 3 && (
                    <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="template-toolbar">
                      <span className="text-xs text-stone-500 uppercase tracking-wider">Templates</span>
                      <Select value="" onValueChange={applyTemplate}>
                        <SelectTrigger data-testid="apply-template-select" className="rounded-xl h-9 text-xs w-56">
                          <SelectValue placeholder={templates.length ? "Apply a saved template…" : "No templates saved yet"} />
                        </SelectTrigger>
                        <SelectContent>
                          {templates.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.name} <span className="text-stone-400">({t.source_object}→{t.target_object})</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button data-testid="save-template-btn" size="sm" variant="outline" onClick={saveAsTemplate}
                        className="gap-1.5 text-xs">
                        <FloppyDisk size={14} /> Save as Template
                      </Button>
                    </div>
                  )}

                  {step === 3 && curOM?.id && (
                    <div className="flex items-center gap-3 mb-5 flex-wrap bg-stone-50 border border-stone-200 rounded-2xl px-4 py-3" data-testid="write-mode-toolbar">
                      <span className="text-xs text-stone-500 uppercase tracking-wider">Write mode</span>
                      <Select value={curOM.write_mode || "insert"} onValueChange={(v) => patchOM(curOM.id, { write_mode: v })}>
                        <SelectTrigger data-testid="write-mode-select" className="rounded-xl h-9 text-xs w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="insert">Insert (create new)</SelectItem>
                          <SelectItem value="update">Update existing</SelectItem>
                          <SelectItem value="upsert">Upsert (create or update)</SelectItem>
                        </SelectContent>
                      </Select>
                      {(curOM.write_mode === "update" || curOM.write_mode === "upsert") && (
                        <>
                          <span className="text-xs text-stone-500 uppercase tracking-wider">Match on</span>
                          <Select value={curOM.match_field || ""} onValueChange={(v) => patchOM(curOM.id, { match_field: v })}>
                            <SelectTrigger data-testid="match-field-select" className="rounded-xl h-9 text-xs w-52">
                              <SelectValue placeholder="Select a target field…" />
                            </SelectTrigger>
                            <SelectContent>
                              {(curFD?.target_fields || []).map((f) => (
                                <SelectItem key={f.name} value={f.name}>{f.label || f.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <span className="text-[11px] text-stone-400 max-w-xs">
                            Previously migrated records match automatically; the match field finds pre-existing ones.
                          </span>
                        </>
                      )}
                    </div>
                  )}

                  {!curFD ? (
                    fieldErr[curOM?.id] ? (
                      <div className="rounded-2xl border border-red-200 bg-red-50 p-5 space-y-3" data-testid="field-load-error">
                        <p className="text-sm text-red-700 font-medium">Couldn't load fields for this object.</p>
                        <p className="text-xs text-red-600 break-words">{fieldErr[curOM.id]}</p>
                        <Button variant="outline" className="rounded-full h-9 text-xs" data-testid="field-retry-btn"
                          onClick={() => { setFieldData((fd) => { const n = { ...fd }; delete n[curOM.id]; return n; }); loadFieldMeta(curOM); }}>
                          Retry
                        </Button>
                      </div>
                    ) : (
                      <p className="text-sm text-stone-400" data-testid="field-loading">Loading fields…</p>
                    )
                  ) : (
                    <div className="space-y-2.5">
                      {curFD.mappings.map((m, idx) => (
                        <div key={idx} className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 bg-stone-50/60 border border-stone-200 rounded-2xl p-3.5 hover:border-stone-300 transition-colors duration-200">
                          {step === 3 ? (
                            <>
                              <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-[0.14em] text-stone-400 mb-1.5">Source Field</div>
                                <Select value={m.source_field || "__none"} onValueChange={(v) => updateMapping(curOM.id, idx, {
                                  source_field: v === "__none" ? null : v,
                                  source_datatype: (curFD.source_fields.find((f) => f.name === v) || {}).datatype,
                                  transform: v === "__none" ? { type: "ignore" } : (m.transform?.type === "ignore" ? { type: "direct" } : m.transform),
                                })}>
                                  <SelectTrigger data-testid={`fm-src-${idx}`} className="rounded-xl h-9 text-xs bg-white"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__none">— Not mapped —</SelectItem>
                                    {curFD.source_fields.map((f) => <SelectItem key={f.name} value={f.name}>{f.label} ({f.name})</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                              <span className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center ${m.source_field ? "bg-orange-50" : "bg-stone-100"}`}>
                                <ArrowRight size={15} weight="bold" className={m.source_field ? "text-[#C2410C]" : "text-stone-300"} />
                              </span>
                              <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-[0.14em] text-stone-400 mb-1.5">Target Field</div>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm text-stone-800 truncate">
                                    {m.target_field}{m.required && <span className="text-[#C2410C] ml-0.5">*</span>}
                                  </span>
                                  <span className="font-mono text-[10px] text-stone-400 bg-white border border-stone-200 rounded-full px-2 py-0.5 shrink-0">{m.target_datatype}</span>
                                </div>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-[0.14em] text-stone-400 mb-1">Field</div>
                                <div className="font-medium text-sm text-stone-800 truncate">
                                  {m.target_field}{m.required && <span className="text-[#C2410C] ml-0.5">*</span>}
                                </div>
                                <div className="text-[11px] text-stone-400 truncate">from {m.source_field || "—"}</div>
                              </div>
                              <span className="w-8 h-8 shrink-0 rounded-full bg-stone-100 flex items-center justify-center">
                                <ArrowRight size={15} weight="bold" className="text-stone-500" />
                              </span>
                              <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-[0.14em] text-stone-400 mb-1.5">Transformation</div>
                                <div className="flex flex-col gap-2">
                                  <Select value={m.transform?.type || "direct"} onValueChange={(v) => updateMapping(curOM.id, idx, { transform: { type: v, params: {} } })}>
                                    <SelectTrigger data-testid={`fm-transform-${idx}`} className="rounded-xl h-9 text-xs bg-white w-40"><SelectValue /></SelectTrigger>
                                    <SelectContent>{TRANSFORMS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                                  </Select>
                                  <TransformParams m={m} onChange={(params) => updateMapping(curOM.id, idx, { transform: { ...m.transform, params } })} />
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </Section>
              )}

              {/* STEP 5 - Validation */}
              {step === 5 && (
                <Section title="Validation" desc="Pre-flight checks before migrating.">
                  <Button data-testid="run-validation-btn" onClick={runValidation} disabled={busy}
                    variant="outline" className="gap-2 mb-6">
                    <CheckCircle size={16} /> {busy ? "Validating…" : validation ? "Re-run Validation" : "Run Validation"}
                  </Button>
                  {!validation ? <p className="text-sm text-stone-400">Running validation…</p> : (
                    <div className="space-y-4">
                      <div className={`flex items-center gap-2 text-sm font-medium ${validation.valid ? "text-emerald-600" : "text-rose-600"}`}>
                        {validation.valid ? <CheckCircle size={18} weight="fill" /> : <XCircle size={18} weight="fill" />}
                        {validation.valid ? "Validation passed — ready to migrate" : "Validation found blocking errors"}
                      </div>
                      {validation.report.map((r) => (
                        <div key={r.object_mapping_id} className="border border-stone-200 rounded-2xl p-4">
                          <div className="font-medium text-sm text-stone-900">{r.mapping} <span className="text-stone-400">({r.field_count} fields)</span></div>
                          {r.issues.length === 0 ? <div className="text-xs text-emerald-600 mt-2 flex items-center gap-1"><CheckCircle size={14} /> No issues</div> : (
                            <ul className="mt-2 space-y-1">
                              {r.issues.map((iss, k) => (
                                <li key={k} className={`text-xs flex items-center gap-1.5 ${iss.severity === "error" ? "text-rose-600" : "text-amber-600"}`}>
                                  {iss.severity === "error" ? <XCircle size={13} /> : <WarningCircle size={13} />}{iss.message}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </Section>
              )}

              {/* STEP 6 - Test Migration */}
              {step === 6 && (
                <Section title="Test Migration" desc="Migrate a small sample (10 records) before the full run.">
                  <Button data-testid="run-test-btn" onClick={runTest} disabled={busy}
                    className="bg-[#C2410C] hover:bg-[#9A3412] gap-2">
                    <Flask size={16} weight="fill" /> {busy ? "Running…" : "Run Test Migration"}
                  </Button>
                  {testJob && (
                    <div className="mt-6 space-y-4">
                      <div className="flex gap-8 text-sm">
                        <Metric label="Status" value={<StatusBadge status={testJob.status} />} />
                        <Metric label="Processed" value={testJob.processed} />
                        <Metric label="Success" value={testJob.successful} cls="text-emerald-600" />
                        <Metric label="Failed" value={testJob.failed} cls="text-rose-600" />
                      </div>
                      {testRecords.length > 0 && (
                        <div className="border border-stone-200 rounded-2xl overflow-x-auto max-h-72 overflow-y-auto">
                          <table className="w-full text-xs">
                            <thead className="sticky top-0 bg-stone-50">
                              <tr className="text-left uppercase tracking-wider text-stone-500 border-b border-stone-200">
                                <th className="px-4 py-2.5">Source ID</th><th className="px-4 py-2.5">Target ID</th>
                                <th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Message</th>
                              </tr>
                            </thead>
                            <tbody>
                              {testRecords.map((r) => (
                                <tr key={r.id} className="border-b border-stone-100">
                                  <td className="px-4 py-1.5 font-mono">{r.source_id}</td>
                                  <td className="px-4 py-1.5 font-mono">{r.target_id || "—"}</td>
                                  <td className="px-4 py-1.5"><StatusBadge status={r.status} /></td>
                                  <td className="px-4 py-1.5 text-rose-600">{r.error || ""}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </Section>
              )}

              {/* STEP 7 - Full Migration */}
              {step === 7 && (
                <Section title="Full Migration" desc="Run the complete migration in the background with checkpoints & retries.">
                  <div className="bg-stone-100 border border-stone-200 rounded-2xl p-4 text-sm text-stone-700 mb-6 leading-relaxed">
                    The migration runs asynchronously. You can monitor progress, pause, resume and download error reports from the job page.
                  </div>
                  <Button data-testid="run-full-btn" onClick={runFull} className="gap-2">
                    <Play size={16} weight="fill" /> Start Full Migration
                  </Button>
                </Section>
              )}
            </div>

            {/* Nav */}
            <div className="flex justify-between mt-6">
              <Button data-testid="wizard-back" variant="outline" onClick={back} disabled={step === 0 || busy}
                className="gap-2"><ArrowLeft size={16} /> Back</Button>
              {step < 7 && (
                <Button data-testid="wizard-next" onClick={next} disabled={busy || (step === 0 && !sourceId)}
                  className="gap-2">
                  {busy ? "Working…" : "Next"} <ArrowRight size={16} />
                </Button>
              )}
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
}

const Section = ({ title, desc, children }) => (
  <div>
    <h3 className="font-head font-semibold text-xl tracking-tight text-stone-900">{title}</h3>
    <p className="text-sm text-stone-500 mt-1.5 mb-7">{desc}</p>
    {children}
  </div>
);

const Metric = ({ label, value, cls }) => (
  <div>
    <div className="text-xs uppercase tracking-wider text-stone-400">{label}</div>
    <div className={`stat-num text-xl font-bold mt-1 ${cls || "text-stone-900"}`}>{value}</div>
  </div>
);

function ConnSelect({ testid, label = "Source", value, onChange, options }) {
  return (
    <div className="max-w-md">
      <Label className="text-xs uppercase tracking-wider text-stone-500">{label} Connection</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="mt-1.5 rounded-xl"><SelectValue placeholder="Select a connection" /></SelectTrigger>
        <SelectContent>
          {options.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.platform}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function ObjSelect({ testid, value, options, onChange }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="rounded-xl h-10 flex-1 bg-white"><SelectValue placeholder="Select object" /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o.name} value={o.name}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

const FILTER_OPS = ["equals", "not_equals", "contains", "not_empty", "is_empty", "in", "gt", "lt"];

function FilterEditor({ omIndex, filters, sourceFields = [], onChange }) {
  const update = (idx, patch) => onChange(filters.map((f, j) => (j === idx ? { ...f, ...patch } : f)));
  const add = () => onChange([...filters, { field: "", op: "equals", value: "" }]);
  const remove = (idx) => onChange(filters.filter((_, j) => j !== idx));
  const noVal = (op) => op === "not_empty" || op === "is_empty";
  const hasFields = sourceFields.length > 0;
  const fieldMeta = (name) => sourceFields.find((f) => f.name === name);

  return (
    <div className="mt-3 ml-9 border-l-2 border-orange-200 pl-4 space-y-2">
      <div className="text-xs text-stone-500">Only migrate source records that match ALL conditions below.</div>
      {filters.map((f, idx) => {
        const meta = fieldMeta(f.field);
        const isPicklist = meta && meta.datatype === "picklist" && (meta.picklist_values || []).length > 0;
        return (
          <div key={idx} className="flex items-center gap-2 flex-wrap">
            {hasFields ? (
              <Select value={f.field || undefined} onValueChange={(v) => update(idx, { field: v, value: "" })}>
                <SelectTrigger data-testid={`filter-field-${omIndex}-${idx}`} className="h-9 text-xs rounded-xl w-56 bg-white"><SelectValue placeholder="Select field" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {sourceFields.map((sf) => <SelectItem key={sf.name} value={sf.name}>{sf.label} ({sf.name})</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Input data-testid={`filter-field-${omIndex}-${idx}`} value={f.field}
                onChange={(e) => update(idx, { field: e.target.value })}
                placeholder="Source field (e.g. Status)" className="h-9 text-xs rounded-xl w-56 font-mono bg-white" />
            )}
            <Select value={f.op} onValueChange={(v) => update(idx, { op: v })}>
              <SelectTrigger data-testid={`filter-op-${omIndex}-${idx}`} className="h-9 text-xs rounded-xl w-32 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>{FILTER_OPS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
            {!noVal(f.op) && (
              isPicklist && f.op !== "in" && f.op !== "contains" ? (
                <Select value={f.value || undefined} onValueChange={(v) => update(idx, { value: v })}>
                  <SelectTrigger data-testid={`filter-value-${omIndex}-${idx}`} className="h-9 text-xs rounded-xl w-48 bg-white"><SelectValue placeholder="Select value" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {meta.picklist_values.map((pv) => <SelectItem key={pv} value={pv}>{pv}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input data-testid={`filter-value-${omIndex}-${idx}`} value={f.value}
                  onChange={(e) => update(idx, { value: e.target.value })}
                  placeholder={f.op === "in" ? "Open,Working" : "Value"} className="h-9 text-xs rounded-xl w-48 bg-white" />
              )
            )}
            <Button variant="ghost" size="sm" onClick={() => remove(idx)}
              className="text-rose-600 hover:bg-rose-50"><Trash size={13} /></Button>
          </div>
        );
      })}
      <Button data-testid={`add-filter-${omIndex}`} variant="outline" size="sm" onClick={add}
        className="gap-1.5 text-xs"><Plus size={13} /> Add Condition</Button>
      {!hasFields && <div className="text-xs text-stone-400">Field list loading… you can also type the field name.</div>}
    </div>
  );
}

function TransformParams({ m, onChange }) {
  const t = m.transform?.type; const p = m.transform?.params || {};
  if (t === "static") return <Input value={p.value || ""} onChange={(e) => onChange({ value: e.target.value })} placeholder="Default value" className="h-9 text-xs rounded-xl w-48 bg-white" />;
  if (t === "concat") return (
    <div className="flex gap-2">
      <Input value={(p.fields || []).join(",")} onChange={(e) => onChange({ ...p, fields: e.target.value.split(",").map((s) => s.trim()) })} placeholder="fieldA,fieldB" className="h-9 text-xs rounded-xl w-40 bg-white" />
      <Input value={p.separator ?? " "} onChange={(e) => onChange({ ...p, separator: e.target.value })} placeholder="sep" className="h-9 text-xs rounded-xl w-16 bg-white" />
    </div>
  );
  if (t === "date_format") return (
    <div className="flex gap-2">
      <Input value={p.input_format || ""} onChange={(e) => onChange({ ...p, input_format: e.target.value })} placeholder="%Y-%m-%d" className="h-9 text-xs rounded-xl w-28 font-mono bg-white" />
      <Input value={p.output_format || ""} onChange={(e) => onChange({ ...p, output_format: e.target.value })} placeholder="%m/%d/%Y" className="h-9 text-xs rounded-xl w-28 font-mono bg-white" />
    </div>
  );
  if (t === "value_map") return <Input value={p._raw || ""} onChange={(e) => { let map = {}; try { map = JSON.parse(e.target.value); } catch (x) {} onChange({ _raw: e.target.value, map }); }} placeholder='{"Y":"Yes"}' className="h-9 text-xs rounded-xl w-48 font-mono bg-white" />;
  if (t === "boolean_map") return <Input value={(p.true_values || ["true", "1", "yes"]).join(",")} onChange={(e) => onChange({ true_values: e.target.value.split(",").map((s) => s.trim()) })} className="h-9 text-xs rounded-xl w-40 bg-white" />;
  if (t === "text") return (
    <Select value={p.op || "trim"} onValueChange={(v) => onChange({ op: v })}>
      <SelectTrigger className="h-9 text-xs rounded-xl w-28 bg-white"><SelectValue /></SelectTrigger>
      <SelectContent>{["trim", "upper", "lower", "title"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );
  return <span className="text-xs text-stone-300">—</span>;
}
